/**
 * Export & Profil.
 *
 * Zwei Abweichungen vom Entwurf, beide gewollt:
 *  - „Alle Mitarbeiter“ erscheint nur für die Verwaltung.
 *  - Wochensoll und Anfangssaldo sind hier nur noch Anzeige. Es sind
 *    vertraglich vereinbarte Werte; wer sein eigenes Soll herunterdrehen
 *    kann, führt kein Arbeitszeitkonto, sondern eine Wunschliste.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  dayKey, de, fullDateLabel, monthKeys, parseDayEntry, weekKeys, weekStart,
  type DayEntry, type Employee,
} from '@zeit/domain';
import { serverNow } from '../lib/clock.ts';
import { api, read } from '../lib/appwrite.ts';
import { buildRows, downloadCsv, openPrintView, periodLabel, slugify } from '../lib/export.ts';
import type { Session } from '../lib/session.ts';
import { Card, Field, Kicker, Page } from '../ui/index.tsx';
import { ReminderCard } from './ReminderCard.tsx';

const x = de.export;
const p = de.profile;

type Range = 'week' | 'month';
type Scope = 'self' | 'all';

export function Export({
  session,
  onToast,
}: {
  session: Session;
  onToast: (text: string, tone?: 'error') => void;
}) {
  const { employee, isAdmin } = session;
  const today = dayKey(serverNow());

  const [range, setRange] = useState<Range>('week');
  const [scope, setScope] = useState<Scope>('self');
  const [busy, setBusy] = useState(false);
  const [rowCount, setRowCount] = useState<number | null>(null);

  const keys = useMemo(
    () => (range === 'month' ? monthKeys(today) : weekKeys(weekStart(today))),
    [range, today],
  );

  /** Holt die Daten für den gewählten Umfang — eigene oder alle. */
  const gather = useCallback(async (): Promise<{
    employees: Employee[];
    byEmployee: Map<string, DayEntry[]>;
  }> => {
    if (scope === 'all') {
      const res = await api.admin.overview(keys[0], keys[keys.length - 1]);
      const byEmployee = new Map<string, DayEntry[]>();
      for (const raw of res.entries) {
        // `parseDayEntry` statt einer zweiten, eigenen Umwandlung: die hier
        // stehende Fassung fiel über jede kaputte Pausen-Spalte, weil sie
        // ohne Absicherung durch `JSON.parse` ging — und der ganze Export
        // scheiterte dann an einem einzigen Datensatz.
        const entry = parseDayEntry(raw);
        const list = byEmployee.get(entry.employeeId) ?? [];
        list.push(entry);
        byEmployee.set(entry.employeeId, list);
      }
      return { employees: res.employees, byEmployee };
    }

    // Für den Saldo braucht es die Historie ab Eintritt, nicht nur den Zeitraum.
    const entries = await read.days(employee.$id!, employee.employmentStart, today);
    return { employees: [employee], byEmployee: new Map([[employee.$id!, entries]]) };
  }, [scope, keys, employee, today]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { employees, byEmployee } = await gather();
        if (!cancelled) setRowCount(buildRows(employees, byEmployee, keys, serverNow()).length);
      } catch {
        if (!cancelled) setRowCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gather, keys]);

  const csv = async () => {
    setBusy(true);
    try {
      const { employees, byEmployee } = await gather();
      const rows = buildRows(employees, byEmployee, keys, serverNow());
      const who = scope === 'all' ? 'alle' : slugify(employee.name);
      downloadCsv(rows, `zeiterfassung-${range === 'month' ? 'monat' : 'woche'}-${who}-${keys[0]}.csv`);
      onToast(de.toast.csv);
    } catch {
      onToast(de.error.generic, 'error');
    } finally {
      setBusy(false);
    }
  };

  const print = async () => {
    setBusy(true);
    try {
      const { employees, byEmployee } = await gather();
      const opened = openPrintView(employees, byEmployee, keys, range, serverNow(), today);
      if (!opened) onToast(x.popupBlocked, 'error');
    } catch {
      onToast(de.error.generic, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page title={de.nav.export}>
      <div className="grid-2">
        <Card title={x.title}>
        <div>
          <Kicker>{x.period}</Kicker>
          <div className="seg" style={{ marginTop: 8 }}>
            <button
              type="button"
              className="seg-btn"
              aria-pressed={range === 'week'}
              onClick={() => setRange('week')}
            >
              {x.periodWeek}
            </button>
            <button
              type="button"
              className="seg-btn"
              aria-pressed={range === 'month'}
              onClick={() => setRange('month')}
            >
              {x.periodMonth}
            </button>
          </div>
        </div>

        {isAdmin && (
          <div>
            <Kicker>{x.scope}</Kicker>
            <div className="seg" style={{ marginTop: 8 }}>
              <button
                type="button"
                className="seg-btn"
                aria-pressed={scope === 'self'}
                onClick={() => setScope('self')}
              >
                {employee.name}
              </button>
              <button
                type="button"
                className="seg-btn"
                aria-pressed={scope === 'all'}
                onClick={() => setScope('all')}
              >
                {x.scopeAll}
              </button>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
          <button
            type="button"
            className="btn btn-primary"
            style={{ minHeight: 64, fontSize: 16 }}
            disabled={busy}
            onClick={csv}
          >
            {x.csv}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ minHeight: 64, fontSize: 16, borderWidth: 2 }}
            disabled={busy}
            onClick={print}
          >
            {x.print}
          </button>
        </div>

        <div className="text-muted" style={{ fontSize: 12 }}>
          {rowCount === null ? '…' : x.summary(rowCount, periodLabel(range, keys))}
        </div>
        </Card>

        <Card title={p.title} aside={employee.name}>
        <div style={{ display: 'grid', gap: 14, maxWidth: 360 }}>
          <ReadOnly label={p.department} value={employee.department || '—'} />
          <ReadOnly label={p.employmentStart} value={fullDateLabel(employee.employmentStart)} />
          <ReadOnly
            label={p.weeklyTarget}
            value={employee.weeklyTargetHours.toFixed(1).replace('.', ',')}
          />
          <ReadOnly label={p.workdays} value={String(employee.workdaysPerWeek)} />
          <ReadOnly
            label={p.openingBalance}
            value={employee.openingBalanceHours.toFixed(2).replace('.', ',')}
          />
        </div>

        <p className="text-muted" style={{ fontSize: 12, maxWidth: '60ch', margin: 0 }}>
          {p.readonlyHint}
        </p>
        <p className="text-muted" style={{ fontSize: 12, maxWidth: '60ch', margin: 0 }}>
          {p.retentionHint}
        </p>
        </Card>

        <ReminderCard onToast={onToast} />
      </div>
    </Page>
  );
}

function ReadOnly({ label, value }: { label: string; value: string }) {
  return (
    <Field label={label}>
      <input className="input" value={value} readOnly disabled style={{ minHeight: 44 }} />
    </Field>
  );
}
